/**
 * @author       matthew@zumper.com (Matthew Talamini)
 * @copyright    Copyright (c) 2021, Zumper
 * @description  loading thunk for ProConData module
 */

import * as Api from '@zcode/nr/es/shared/api'
import { logger } from '../../../../../logger'

import type { Dispatch } from '../../../../types'
import {
  clearProConData,
  doneFetchingProConData,
  errorFetchingProConData,
  receiveProConData,
  startFetchingProConData,
} from '../actions/index'

interface LoadProConDataPropsAddress {
  addressId: number
  buildingId?: never
}

interface LoadProConDataPropsBuilding {
  addressId?: never
  buildingId: number
}

export type LoadProConDataProps = LoadProConDataPropsAddress | LoadProConDataPropsBuilding

export const loadProConData =
  ({ addressId, buildingId }: LoadProConDataProps) =>
  async (dispatch: Dispatch) => {
    if (addressId && buildingId) {
      throw new Error('loadProConData requires either addressId or buildingId, both provided')
    }

    if (addressId) {
      const key = `a-${addressId}` as const
      dispatch(startFetchingProConData({ key }))
      try {
        const proConData = await Api.proConV2AddressData.get({ addressId })
        dispatch(clearProConData())
        dispatch(receiveProConData(proConData))
      } catch (e: unknown) {
        dispatch(errorFetchingProConData(e))
        logger.warn('failed to load pro con data for address', { addressId }, e)
      } finally {
        dispatch(doneFetchingProConData({ key }))
      }
    } else if (buildingId) {
      const key = `b-${buildingId}` as const
      dispatch(startFetchingProConData({ key }))
      try {
        const proConData = await Api.proConV2BuildingData.get({ buildingId })
        dispatch(clearProConData())
        dispatch(receiveProConData(proConData))
      } catch (e: unknown) {
        dispatch(errorFetchingProConData(e))
        logger.warn('failed to load pro con data for building', { buildingId }, e)
      } finally {
        dispatch(doneFetchingProConData({ key }))
      }
    } else {
      throw new Error('loadProConData requires either addressId or buildingId, neither provided')
    }
  }
